import dayjs from 'dayjs';

/**
 * Wrapper for Datetime Utility
 *  - based off of datetime wrapper in support-api
 */
export default class Datetime {
  datetime: Date;

  value: Date;

  constructor(datetime?: string | number | Date) {
    this.datetime = datetime ? new Date(datetime) : new Date();

    if (!dayjs(this.datetime).isValid()) {
      throw new Error(
        '[Datetime] The given value could not be successfully converted to a valid Date object',
      );
    }

    this.value = this.datetime;
  }

  /**
   * Get the formatted date according to the string of tokens passed in.
   * @param formatString - Format
   * https://day.js.org/docs/en/display/format#list-of-all-available-formats
   * @returns formatted date
   */
  format(formatString: string): string {
    try {
      const formattedDate = dayjs(this.value).format(formatString);
      return formattedDate;
    } catch (err) {
      return this.value.toLocaleString();
    }
  }

  /**
   * Get the day of the week
   * @returns number - The day of the week (0 to 6).
   */
  getDay(): number {
    return dayjs(this.value).day();
  }

  toLocal(): Date {
    return this.value;
  }

  /**
   * returns true if date matches current date.
   * @param compareTo {Date}
   * @returns boolean
   */
  isSameDay(compareTo: Date): boolean {
    return dayjs(this.value).isSame(compareTo, 'date');
  }

  /**
   * Get relative time from current date
   */
  getRelativeTimeFromToday = (): string => {
    const relativeDays = {
      0: 'Yesterday',
      7: 'This week',
      14: 'More than a week ago',
      default: 'More than two weeks ago',
    };

    // From date should be from time = 00:00:00
    const dateFrom = new Date();
    dateFrom.setHours(0);
    dateFrom.setMinutes(0);
    dateFrom.setSeconds(0);
    dateFrom.setMilliseconds(0);

    const daysPastFromDatetime = Math.floor(
      (dateFrom.getTime() - this.datetime.getTime()) / (1000 * 60 * 60 * 24),
    );

    if (daysPastFromDatetime > 14) {
      return relativeDays.default;
    }
    if (daysPastFromDatetime <= 14 && daysPastFromDatetime > 7) {
      return relativeDays[14];
    }
    if (daysPastFromDatetime <= 7 && daysPastFromDatetime >= 2) {
      return relativeDays[7];
    }

    if (daysPastFromDatetime < 0) {
      return 'Today';
    }
    return relativeDays[0];
  };
}
